import { SelectTaskListPopover } from "@jugl-web/domain-resources/tasks/components/SelectTaskListPopover";
import { useTaskObjectNaming } from "@jugl-web/domain-resources/tasks/hooks/useTaskObjectNaming";
import { TaskPermissions } from "@jugl-web/domain-resources/tasks/hooks/useTaskPermissions";
import { UserRole } from "@jugl-web/rest-api";
import { PreviewTask } from "@jugl-web/rest-api/tasks";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { cx, useTranslations } from "@jugl-web/utils";
import { useCallback } from "react";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";
import { ReactComponent as ListIcon } from "./assets/list.svg";

interface TimeLogFormTaskSelectProps {
  entityId: string;
  boardId: string | null;
  meId: string;
  userRole: UserRole | undefined;
  selectedTask: { id: string; name: string } | null;
  isEditable: boolean;
  error?: string;
  onSelect: (task: PreviewTask) => void;
  onRequestCreateTask?: () => void;
}

export const TimeLogFormTaskSelect = ({
  meId,
  entityId,
  boardId,
  userRole,
  selectedTask,
  isEditable,
  error,
  onSelect,
  onRequestCreateTask,
}: TimeLogFormTaskSelectProps) => {
  const { t } = useTranslations();

  const taskObjectNaming = useTaskObjectNaming({
    entityId,
    boardId,
  });

  const filterTaskFn = useCallback(
    (_: PreviewTask, taskPermissions: TaskPermissions) =>
      taskPermissions.canLogTime,
    []
  );

  return (
    <div className={error && "bg-grey-100 relative rounded-lg"}>
      <SelectTaskListPopover
        entityId={entityId}
        meId={meId}
        boardId={boardId}
        userRole={userRole}
        selectedTaskId={selectedTask?.id}
        placement="bottom-start"
        filterTaskFn={filterTaskFn}
        onSelect={onSelect}
        onRequestCreateTask={onRequestCreateTask}
        renderTrigger={({ Trigger, triggerRef, isOpen: isPopoverOpen }) => (
          <Trigger
            ref={triggerRef}
            as={InteractiveContainer}
            isDisabled={!isEditable}
            className={cx(
              "font-secondary flex h-10 w-full items-center gap-2 rounded-lg px-2 text-[15px] transition-colors",
              isEditable
                ? isPopoverOpen
                  ? "bg-grey-100"
                  : "hover:bg-grey-100"
                : undefined
            )}
          >
            <ListIcon className="shrink-0" />
            <span className="text-dark-600">{taskObjectNaming.singular}:</span>
            <div className="flex items-center gap-1 overflow-hidden">
              <span className="text-dark-900 truncate font-medium">
                {selectedTask
                  ? selectedTask.name
                  : t({
                      id: "tasks-page.select-task",
                      defaultMessage: "Select Task",
                    })}
              </span>
              {isEditable && (
                <ChevronDownIcon
                  className={cx(
                    "shrink-0 transition-transform",
                    isPopoverOpen && "rotate-180"
                  )}
                />
              )}
            </div>
          </Trigger>
        )}
      />
      {error && (
        <span className="text-tertiary-700 font-secondary pointer-events-none absolute top-1/2 right-0 block -translate-y-1/2 transform px-2 text-xs">
          {error}
        </span>
      )}
    </div>
  );
};
